/**
 * Product analytics behind one small API so components never talk to the vendor directly.
 * Plausible (EU, cookieless, no personal data), loaded through the same-origin proxy `/pl`
 * (vite.config.ts, deploy/Caddyfile) so ad blockers do not drop it. Nothing loads and nothing is
 * sent without VITE_PLAUSIBLE_DOMAIN (dev, CI, e2e). The same setup as PrestoCRM.
 *
 *   track("document_booked", { kind: "ER" })   custom events (a goal in Plausible each)
 *   trackPageview(pathname)                    page views, sent by <PageViews /> in App.tsx
 */
import { env } from "@/lib/platform/env";

export type EventProps = Record<string, string | number | boolean | null | undefined>;

type Plausible = ((event: string, options?: { u?: string; props?: EventProps }) => void) & {
  q?: unknown[];
};

declare global {
  interface Window {
    plausible?: Plausible;
  }
}

/** The manual variant: page views come from trackPageview, so ids and tokens never leave. */
export const PLAUSIBLE_SCRIPT = "/pl/js/script.manual.js";

/** Call once at startup (main.tsx): adds Plausible's script and a queue for early events. */
export function initAnalytics(): void {
  if (!env.plausibleDomain || document.querySelector(`script[src="${PLAUSIBLE_SCRIPT}"]`)) return;
  window.plausible ??= (...args: unknown[]) => {
    (window.plausible!.q ??= []).push(args);
  };
  const script = document.createElement("script");
  script.defer = true;
  script.dataset.domain = env.plausibleDomain;
  script.dataset.api = "/pl/api/event";
  script.src = PLAUSIBLE_SCRIPT;
  document.head.appendChild(script);
}

/** Props must never carry personal data (names, emails, amounts, ids): counts, kinds and flags only. */
export function track(event: string, props?: EventProps): void {
  if (!env.plausibleDomain) return;
  // Events carry the page too; without `u` the script would send the full address.
  window.plausible?.(event, { u: currentPage(), ...(props ? { props } : {}) });
}

export function trackPageview(pathname: string): void {
  if (!env.plausibleDomain) return;
  window.plausible?.("pageview", { u: window.location.origin + pagePath(pathname) });
}

function currentPage(): string {
  return window.location.origin + pagePath(window.location.pathname);
}

/**
 * The path Plausible sees: record ids (PocketBase: 15 characters a-z0-9) and the tokens of
 * invitation and email links become placeholders, so `/documents/:id` is one page and secrets
 * stay here. No query string.
 */
export function pagePath(pathname: string): string {
  return pathname
    .replace(/^\/invite\/[^/]+/, "/invite/:token")
    .replace(
      /^\/auth\/(confirm-verification|confirm-password-reset|confirm-email-change)\/[^/]+/,
      "/auth/$1/:token",
    )
    .replace(/\/[a-z0-9]{15}(?=\/|$)/g, "/:id");
}
